"use client";
import {useEffect, useRef, useState} from "react";
import {useTranslations} from "next-intl";
import {tv} from "tailwind-variants";

const generateClasses = tv({
    slots: {
        root: 'group/video relative max-w-full overflow-hidden',
        video: 'w-fill object-cover',
        controls: 'absolute bottom-m right-m z-10',
        button: 'flex items-center justify-center w-[50px] h-[50px] rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors',
        icon: 'w-[24px] h-[24px]',
    },
});

interface Props {
    srcDefault: string
    srcTablet?: string
    srcDesktop?: string
    poster?: string
    altText?: string
    subtitleSrc?: string
    subtitleLabel?: string
    subtitleLanguageCode?: string
    className?: string
    autoplay?: boolean
    loop?: boolean
    muted?: boolean
    controlsType?: 'custom' | 'native' | 'none'
}

export default function VideoPlayer({srcDefault, srcTablet, srcDesktop, poster, altText, subtitleSrc, subtitleLabel, subtitleLanguageCode, className, autoplay = true, loop = true, muted = true, controlsType = 'custom'}: Props) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const t = useTranslations("Defaults");
    const [activeSrc, setActiveSrc] = useState(srcDefault);
    const [isPlaying, setIsPlaying] = useState(autoplay);
    const {root, video, controls, button, icon} = generateClasses();
    const pauseLabel = t.has("videoPause") ? t("videoPause") : "Video pausieren";
    const playLabel = t.has("videoPlay") ? t("videoPlay") : "Video abspielen";

    const togglePlayPause = () => {
        if (!videoRef.current) return;
        if (videoRef.current.paused) {
            void videoRef.current.play();
        } else {
            videoRef.current.pause();
        }
    };

    useEffect(() => {
        const updateSrc = () => {
            if (srcDesktop && window.innerWidth >= 1024) setActiveSrc(srcDesktop);
            else if (srcTablet && window.innerWidth >= 768) setActiveSrc(srcTablet);
            else setActiveSrc(srcDefault);
        };
        updateSrc();
        window.addEventListener('resize', updateSrc);
        return () => window.removeEventListener('resize', updateSrc);
    }, [srcDefault, srcTablet, srcDesktop]);

    return (
        <div className={root()}>
            <video
                ref={videoRef}
                src={activeSrc}
                poster={poster}
                muted={muted}
                loop={loop}
                playsInline
                autoPlay={autoplay}
                preload="metadata"
                aria-label={altText}
                controls={controlsType === 'native'}
                crossOrigin={subtitleSrc ? "anonymous" : undefined}
                className={video({className})}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
            >
                {subtitleSrc && subtitleLabel && subtitleLanguageCode && (
                    <track kind="subtitles" src={subtitleSrc} label={subtitleLabel} srcLang={subtitleLanguageCode} default />
                )}
            </video>
            {controlsType === 'custom' && (
                <div className={controls()}>
                    <button
                        type="button"
                        className={button()}
                        onClick={togglePlayPause}
                        aria-pressed={isPlaying}
                        aria-label={isPlaying ? pauseLabel : playLabel}
                    >
                        {isPlaying ? (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={icon()}>
                                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={icon()}>
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}
